import { useChat } from '@ai-sdk/react';
import { MooAnalyticsEvents } from '@cowbell/data/analytics';
import { Alert, Box, Button, Center, Loader, Stack } from '@cowbell/ui/core';
import { useQueryClient } from '@tanstack/react-query';
import { DefaultChatTransport } from 'ai';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Context } from '../../integrations/context-types';
import {
  useMooChatContext,
  useMooConfig,
  useShellAgents,
  useShellContexts,
  useShellError,
  useShellPane,
  useShellSelection,
} from '../../provider';
import {
  keyConversation,
  useAgentSlashCommands,
  useLoadConversation,
} from '../../provider/queries';
import { selectPendingReplay } from '../../state/selectors';
import {
  useUploads,
  type UploadEntry,
  type UseUploadsApi,
} from '../../uploads/use-uploads';
import { useFileDrop } from '../../uploads/use-file-drop';
import { toCanonicalUuid } from '../../utils/canonical-uuid';
import { Composer } from '../Composer/Composer';
import { ContextChipList } from '../Composer/ContextChipList';
import { ConnectedAppsChipStrip, ToneSelect } from '../ConnectedApps';
import { MessageList } from '../MessageList/MessageList';
import { buildAttachmentsPart } from '../MessageList/message-text';
import { ShellDrawer } from '../ShellDrawer/ShellDrawer';
import { UploadChipStrip } from '../UploadChipStrip/UploadChipStrip';
import { surface } from '../Motion';
import classes from './ChatPanel.module.css';

const WEB_SEARCH_STORAGE_KEY = 'moo:web-search-enabled';
import { EmptyState } from './EmptyState';
import { readTurnToolNames } from './turn-tool-names';
import { AppendAssistantTurnProvider } from './chat-append-assistant-turn-context';
import { ChatSendProvider } from './chat-send-context';
import { markdownToPlainText } from '../../markdown/markdown-to-plain-text';
import type { NextPromptInput } from '../../types/transport';
import { nextPromptInputFor } from './next-prompt-input';

export interface ChatPanelProps {
  readonly paneId?: string;
  /** Hoisted by the caller (`MooChat`) so upload state survives this panel's
   *  own remount when `selection.conversationId` binds after the first reply
   *  — `MooChat` keys `ChatPanel` by conversationId, so anything owned
   *  *inside* this subtree is destroyed on that transition (Bugbot,
   *  OMNI-584). Falls back to an internal instance when omitted, e.g. in
   *  tests that render `ChatPanel` standalone. */
  readonly uploads?: UseUploadsApi;
}

function uploadsToDocumentContexts(uploads: UploadEntry[]): Context[] {
  const out: Context[] = [];
  for (const u of uploads) {
    if (u.status !== 'done' || !u.uploaded) continue;
    out.push({
      kind: 'document',
      fileName: u.uploaded.name,
      fileType: u.uploaded.mimeType,
      fileSize: u.uploaded.size,
      // Wire shape carries an opaque url field — the server's
      // `transport.uploadFile` returns an id, which is what we forward
      // here. Hosts that need a real URL can override `buildRequestBody`
      // and look the id up against their own document store.
      url: u.uploaded.id,
      extractedContent: u.uploaded.extractedText,
      // V1 parity: forward s3Path so the chat-agent backend can resolve
      // the attachment from a context-only payload.
      s3Path: u.uploaded.s3Path,
    });
  }
  return out;
}

/**
 * Extracts a conversationId from the last `data-done` part of an assistant
 * message, if present. AI SDK may not surface it at message-level metadata.
 */
function readDoneConversationId(
  parts?: Array<{ type: string; data?: any }>
): string | null {
  if (!parts) return null;
  for (let i = parts.length - 1; i >= 0; i--) {
    const part = parts[i];
    if (part?.type === 'data-done') {
      const cid = part.data?.conversationId;
      return typeof cid === 'string' ? cid : null;
    }
  }
  return null;
}

interface ChatStreamProps {
  readonly paneId: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly initialMessages: any[];
  readonly uploads: UseUploadsApi;
}

/**
 * Owns the live `useChat` session for the currently-selected conversation.
 *
 * Mounted by `ChatPanel` only after history hydration completes (or when
 * the conversation is fresh and there is no history to load). AI SDK v5
 * reads `messages` once on the initial render, so the parent must supply
 * the fully-resolved initial set before this component mounts — otherwise
 * an `[]` snapshot taken during hydration would lock in and the fetched
 * history would never reach the chat.
 */
function ChatStream({ paneId, initialMessages, uploads }: ChatStreamProps) {
  const config = useMooConfig();
  const {
    logger,
    analytics,
    transport,
    toast,
    tones,
    voiceInput,
    onTurnSucceeded,
  } = config;
  const transportConfig = config.transport.chat;
  const [isImproving, setIsImproving] = useState(false);

  const selection = useShellSelection();
  const agents = useShellAgents();
  const pane = useShellPane(paneId);
  const contexts = useShellContexts();
  const conversationKey = selection.conversationId ?? 'fresh';
  const errorState = useShellError(conversationKey);
  const slashCommands = useAgentSlashCommands();
  const queryClient = useQueryClient();
  const { state, dispatch } = useMooChatContext();
  const pendingReplay = selectPendingReplay(state);

  const {
    conversationId: selectedConversationId,
    selectConversation,
    resetNonce,
  } = selection;
  const { clear: clearError } = errorState;

  // Agent the in-flight request was actually sent under — captured at send
  // time so `onFinish` can catalog the finished conversation under the
  // right agent even if the user switches agents while a fresh (unkeyed)
  // stream is still in flight.
  const sentAgentIdRef = useRef<string | null>(null);

  // Conversation id the in-flight request was sent under — captured at send
  // time so `onFinish` can tell the transport which conversation's staged
  // send-state to commit (e.g. delivered-context tracking). Matches the id
  // passed to `buildRequestBody`; null for a brand-new conversation.
  const sentConversationIdRef = useRef<string | null>(null);

  // resetNonce captured at send time. New Chat and agent-switch both bump
  // it; if it advances before a fresh-chat stream finishes, the user has
  // moved on and `onFinish` must NOT bind this panel to the just-finished
  // conversation. The send-time-agent guard alone catches agent-switch
  // (agentId changes) but misses New Chat, which keeps the same agent.
  // `onFinish` compares against the live `resetNonce` read directly (as it
  // already does with `selection.agentId`) — deliberately NOT a ref updated
  // in an effect, which would lag the render that bumps resetNonce and let a
  // stream settling in that window rebind the abandoned chat (Bugbot). AI
  // SDK v5 refreshes its onFinish callbacksRef during render, so the latest
  // closure — and thus the current resetNonce — is what runs at finish time.
  const sentResetNonceRef = useRef(resetNonce);

  const chatTransport = useMemo(
    () =>
      new DefaultChatTransport({
        api: transportConfig.endpoint,
        headers: () => transportConfig.getHeaders(),
      }),
    [transportConfig]
  );

  // The guessed next prompt lives in pane state, not here: the first reply of
  // a fresh chat re-keys this component (MooChat keys ChatPanel by
  // conversationId), so local state would be gone before the guess arrives.
  // A guess that lands after the user typed, sent, or dismissed is dropped
  // by the reducer's request-id check; the abort just saves the round trip.
  const nextPromptAbortRef = useRef<AbortController | null>(null);
  const { requestNextPrompt, receiveNextPrompt, clearNextPrompt } = pane;
  const fetchNextPrompt = useCallback(
    (input: NextPromptInput) => {
      if (!transport.suggestNextPrompt) return;
      nextPromptAbortRef.current?.abort();
      const controller = new AbortController();
      nextPromptAbortRef.current = controller;
      const requestId = crypto.randomUUID();
      requestNextPrompt(requestId);
      transport
        .suggestNextPrompt(input, controller.signal)
        .then((text) =>
          receiveNextPrompt(
            requestId,
            text ? { conversationId: input.conversationId, text } : null
          )
        )
        .catch(() => receiveNextPrompt(requestId, null));
    },
    [transport, requestNextPrompt, receiveNextPrompt]
  );
  const dismissNextPrompt = useCallback(() => {
    nextPromptAbortRef.current?.abort();
    clearNextPrompt();
  }, [clearNextPrompt]);

  const onError = useCallback(
    (err: unknown) => {
      logger.error('moo.stream.error', { error: String(err) });
      errorState.set(String(err));
    },
    [logger, errorState.set]
  );

  const onFinish = useCallback(
    (result: {
      message?: { id?: string; parts?: Array<{ type: string; data?: any }> };
      messages?: ReadonlyArray<unknown>;
      // AI SDK runs onFinish on the finally path — set for cancel, network
      // drop, and error alike, not just clean completion.
      isAbort?: boolean;
      isDisconnect?: boolean;
      isError?: boolean;
    }) => {
      const message = result?.message;
      const parts = message?.parts;
      // The stream hands the id back stripped of dashes; the REST conversation
      // catalog (and the shell's `byId` state) uses the canonical dashed form.
      // Canonicalizing here — once, at the source — keeps every downstream
      // consumer (reducer key, query cache key, delete/get calls) on the same
      // id for a given conversation instead of splitting it into two.
      const rawConversationId = readDoneConversationId(parts);
      const conversationId =
        rawConversationId !== null ? toCanonicalUuid(rawConversationId) : null;
      const messageId: string = message?.id ?? '';
      const correlationId: string | undefined = parts?.findLast?.(
        (p: { type: string; data?: any }) => p.type === 'data-done'
      )?.data?.correlationId;

      logger.debug('moo.stream.done', {
        messageId,
        conversationId,
        correlationId,
      });

      clearError();

      // Confirm the send to the transport so it can commit any per-send state
      // it staged in `buildRequestBody` (e.g. which contexts the backend has
      // now seen). Keyed on the send-time id, matching what was staged. Guard
      // against the abort/disconnect/error finishes — onFinish fires on those
      // too, and committing there would suppress the retry's tags for a turn
      // the backend may never have kept (Bugbot).
      if (!result.isAbort && !result.isDisconnect && !result.isError) {
        transportConfig.onSendSucceeded?.(sentConversationIdRef.current);
        onTurnSucceeded?.({ toolNames: readTurnToolNames(parts) });
        const nextPromptInput = nextPromptInputFor(
          conversationId,
          message,
          result.messages ?? []
        );
        if (nextPromptInput) fetchNextPrompt(nextPromptInput);
      }

      if (conversationId !== null && selectedConversationId === null) {
        // Use the agent the request was actually sent under, not the live
        // selection — the user may have switched agents while this
        // (unkeyed, fresh-chat) stream was still in flight.
        const sentAgentId = sentAgentIdRef.current;

        // Drop the new conversation into the RECENT list immediately —
        // otherwise the drawer only picks it up once the 60s query
        // staleTime lapses or an unrelated invalidate (e.g. delete) fires.
        // Cataloged under sentAgentId regardless of any later switch, so
        // the history stays attributed to whoever actually handled it.
        if (sentAgentId) {
          dispatch({
            type: 'CONVERSATION_FINISHED',
            conversationId,
            agentId: sentAgentId,
            ref: {
              id: conversationId,
              agentId: sentAgentId,
              title: '',
              updatedAt: new Date().toISOString(),
            },
          });

          // Refetch the catalog so the real title (from the server) replaces
          // this optimistic empty one as soon as possible, instead of sitting
          // as "Untitled conversation" until the list's staleTime lapses.
          queryClient.invalidateQueries({ queryKey: ['moo', 'conversations'] });
        }

        // Only bind this panel to the new conversation if the live
        // selection still matches who sent it. `conversationId` belongs to
        // `sentAgentId`'s backend session — pairing it with a since-switched
        // selection.agentId would make the panel look consistent (an agent
        // + a conversation both "selected") while actually mismatched, and
        // a follow-up send would target the wrong agent for that
        // conversation id.
        // ...and only if the user hasn't reset since sending. New Chat keeps
        // the same agentId, so the guard above passes on its own — the
        // resetNonce comparison is what separates "still on the fresh chat I
        // sent from" from "clicked New Chat while this stream was in flight",
        // where binding would yank the abandoned conversation back onscreen
        // instead of leaving the blank chat the user asked for (Bugbot).
        if (
          sentAgentId === selection.agentId &&
          sentResetNonceRef.current === resetNonce
        ) {
          // Seed the conversation cache with the live message list *before*
          // dispatching selection so the panel's re-key (MooChat re-renders
          // with the new conversationId as key) sees the new conversation
          // as already-hydrated. Without this, useLoadConversation refetches
          // and either flashes a loader or races the backend persistence —
          // sometimes returning 404 and erasing the message the user just
          // sent.
          queryClient.setQueryData(keyConversation(conversationId), {
            id: conversationId,
            title: '',
            messages: result.messages ?? [],
          });
          selectConversation(conversationId);
        }
      }
    },
    [
      logger,
      queryClient,
      dispatch,
      selectedConversationId,
      selection.agentId,
      resetNonce,
      selectConversation,
      clearError,
      transportConfig,
      onTurnSucceeded,
      fetchNextPrompt,
    ]
  );

  // `onData` is declared inside the `useChat` options, before `chat` exists.
  const setMessagesRef = useRef<
    ReturnType<typeof useChat>['setMessages'] | undefined
  >(undefined);

  const chat = useChat({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    transport: chatTransport as any,
    onError,
    onFinish,
    onData: (part) => {
      // Moo's emoji reaction to the user's message (chat-agent sends it as a
      // transient part, so it never lands in the assistant message). It always
      // belongs to the latest user message of this turn.
      if (part.type !== 'data-reaction') return;
      const emoji = (part.data as { emoji?: unknown } | undefined)?.emoji;
      if (typeof emoji !== 'string' || !emoji) return;
      setMessagesRef.current?.((prev) => {
        const idx = prev.findLastIndex((m) => m.role === 'user');
        const target = prev[idx];
        if (!target) return prev;
        const next = [...prev];
        next[idx] = {
          ...target,
          parts: [...target.parts, { type: 'data-reaction', data: { emoji } }],
        };
        return next;
      });
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    messages: initialMessages as any,
  });
  setMessagesRef.current = chat.setMessages;

  // Abort any in-flight stream when this component unmounts. The parent
  // re-keys on conversation switch, which unmounts this instance — without
  // an explicit stop, AI SDK v5 leaves the prior request running.
  const { stop } = chat;
  useEffect(() => {
    return () => {
      stop();
    };
  }, [stop]);

  const isStreaming =
    chat.status === 'submitted' || chat.status === 'streaming';

  // Append a synthetic assistant turn so A2UI surface-action results land in
  // the live chat (the /surfaces/action endpoint isn't on the SSE stream, so
  // the BE-persisted confirmation row wouldn't otherwise show until refresh).
  const { setMessages } = chat;
  const appendAssistantTurn = useCallback(
    (text: string) => {
      if (!text) return;
      setMessages((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          role: 'assistant',
          parts: [{ type: 'text', text }],
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- AI SDK UIMessage shape
        } as any,
      ]);
    },
    [setMessages]
  );

  // New Chat / agent-switch reset the shared selection's conversationId to
  // null — but if this session's own conversationId was already null (the
  // user stopped a response before the server assigned one), that's a
  // null-to-null non-change: MooChat's `key={conversationId ?? 'fresh'}`
  // doesn't change either, so this ChatStream/useChat instance survives
  // instead of remounting, and the stopped stream's stale message/status
  // is left stuck on screen (OMNI-628). `resetNonce` changes on every such
  // reset regardless, so it's what this effect keys off of. It only clears
  // when nothing is in flight — a reset while a request could still finish
  // and bind (e.g. switching agents mid-stream) must NOT be torn down here.
  const lastHandledResetNonceRef = useRef(resetNonce);
  useEffect(() => {
    if (resetNonce === lastHandledResetNonceRef.current) return;
    // Don't mark this nonce handled until it's actually acted on — a reset
    // that lands mid-stream must keep re-checking on every subsequent
    // render until the request settles, not be silently dropped the first
    // time it's seen while still streaming.
    if (isStreaming) return;
    lastHandledResetNonceRef.current = resetNonce;
    setMessages([]);
    clearError();
  }, [resetNonce, isStreaming, setMessages, clearError]);

  // Uploads get their own, separate reset-tracking ref — deliberately NOT
  // gated on `isStreaming` like the effect above. That gate exists so a
  // still-arriving response isn't dropped before it can bind; an attachment
  // has no such constraint. Gating it the same way meant a New Chat that
  // landed mid-stream deferred the clear until the stream settled, which
  // could wipe a file the user had since attached to the *new* fresh chat
  // in the meantime (Bugbot, OMNI-584). When `uploads` is hoisted into
  // `MooChat` (the production path), that component already clears
  // immediately on the same `resetNonce` bump — this is only load-bearing
  // for the standalone/fallback instance a bare `ChatPanel` owns itself.
  const lastHandledUploadsResetNonceRef = useRef(resetNonce);
  useEffect(() => {
    if (resetNonce === lastHandledUploadsResetNonceRef.current) return;
    lastHandledUploadsResetNonceRef.current = resetNonce;
    uploads.clear();
  }, [resetNonce, uploads.clear]);

  const handleSubmit = useCallback(
    (text: string, skill?: string, extraContexts: readonly Context[] = []) => {
      if (!selection.agentId) return;

      const messageId = crypto.randomUUID();
      const { agentId } = selection;
      // A file attached before any conversation exists gets attributed to a
      // provisional conversationId by the upload endpoint (see useUploads);
      // reuse it here so the send lands in the same backend thread instead
      // of minting a second, orphaned one (OMNI-584).
      const conversationId =
        selection.conversationId ?? uploads.pendingConversationId;
      sentAgentIdRef.current = agentId;
      sentConversationIdRef.current = conversationId;
      sentResetNonceRef.current = resetNonce;

      // Promote completed uploads to DocumentContext entries — appended to
      // the merged user/derived contexts for this send only. In-flight or
      // errored uploads are dropped (the chip strip surfaces the state to
      // the user; sending half-uploaded files would race the upload API).
      const uploadContexts = uploadsToDocumentContexts(uploads.uploads);
      const mergedWithUploads = [
        ...contexts.merged,
        ...extraContexts,
        ...uploadContexts,
      ];

      logger.debug('moo.stream.start', { messageId, agentId, conversationId });
      analytics.track(MooAnalyticsEvents.MESSAGE_SENT, {
        agentId,
        conversationId: conversationId ?? '',
        contextCount: mergedWithUploads.length,
      });

      clearError();
      dismissNextPrompt();

      const body = transportConfig.buildRequestBody({
        messageId,
        agentId,
        conversationId,
        text,
        contexts: mergedWithUploads,
        ...(skill ? { skill } : {}),
        ...(pane.toneId ? { tone: pane.toneId } : {}),
        // Only the off state travels; the backend's default is on.
        ...(pane.webSearchEnabled === false ? { webSearch: false } : {}),
      });

      const sentUploads = uploads.uploads.filter(
        (u) => u.status === 'done' && u.uploaded
      );
      // The files ride in the body as document contexts; this part only lets
      // the bubble show them. Plain text sends keep the simple shape.
      chat.sendMessage(
        sentUploads.length > 0
          ? {
              parts: [
                { type: 'text', text },
                buildAttachmentsPart(sentUploads.map((u) => u.name)),
              ],
            }
          : { text },
        { body }
      );
      uploads.discard(sentUploads.map((u) => u.id));
      pane.setComposerText('');
    },
    [
      logger,
      analytics,
      transportConfig,
      selection.agentId,
      selection.conversationId,
      resetNonce,
      contexts.merged,
      uploads.uploads,
      uploads.discard,
      uploads.pendingConversationId,
      chat.sendMessage,
      pane.setComposerText,
      pane.toneId,
      pane.webSearchEnabled,
      clearError,
      dismissNextPrompt,
    ]
  );

  // The web-search switch persists per user in this browser. Restore once per
  // mount; writes happen in the onChange below. Storage can be absent or
  // blocked, so both sides fail quietly.
  const { setWebSearchEnabled } = pane;
  useEffect(() => {
    try {
      const stored = globalThis.localStorage?.getItem(WEB_SEARCH_STORAGE_KEY);
      if (stored === 'false') setWebSearchEnabled(false);
    } catch {
      /* storage unavailable */
    }
  }, [setWebSearchEnabled]);
  const handleWebSearchChange = useCallback(
    (enabled: boolean) => {
      setWebSearchEnabled(enabled);
      try {
        globalThis.localStorage?.setItem(
          WEB_SEARCH_STORAGE_KEY,
          String(enabled)
        );
      } catch {
        /* storage unavailable */
      }
    },
    [setWebSearchEnabled]
  );

  // Switch + replay: when a specialist-switch navigation card calls
  // selectAgent(id, replayQuery), the original question is queued in
  // shell state. Fire it once the new agent has landed in selection
  // and we aren't already streaming, so the replay reaches the freshly
  // selected agent. The CONSUMED dispatch lands in the same batch as
  // the send, so a re-render can't trigger a second submit.
  useEffect(() => {
    if (!pendingReplay) return;
    if (!selection.agentId) return;
    if (isStreaming) return;
    handleSubmit(pendingReplay);
    dispatch({ type: 'MESSAGE_REPLAY_CONSUMED' });
  }, [pendingReplay, selection.agentId, isStreaming, handleSubmit, dispatch]);

  const handleCancel = useCallback(() => {
    logger.debug('moo.stream.cancel', { messageId: '' });
    analytics.track(MooAnalyticsEvents.MESSAGE_CANCELLED, {
      agentId: selection.agentId ?? '',
      conversationId: selection.conversationId ?? '',
    });
    chat.stop();
  }, [
    logger,
    analytics,
    selection.agentId,
    selection.conversationId,
    chat.stop,
  ]);

  const { setComposerText } = pane;
  const handleComposerChange = useCallback(
    (text: string) => {
      // The reducer retires the guess as soon as the draft is non-empty.
      if (text !== '') nextPromptAbortRef.current?.abort();
      setComposerText(text);
    },
    [setComposerText]
  );

  // Only offer the guess in the conversation it was made for.
  const nextPromptText =
    pane.nextPrompt !== null &&
    selectedConversationId !== null &&
    toCanonicalUuid(pane.nextPrompt.conversationId).toLowerCase() ===
      toCanonicalUuid(selectedConversationId).toLowerCase()
      ? pane.nextPrompt.text
      : undefined;

  const hasMessages = chat.messages.length > 0;
  const selectedAgent = selection.agentId
    ? agents.byId[selection.agentId] ?? null
    : null;

  const handleStarterClick = useCallback(
    (label: string, pageContext?: Context) => {
      if (!pageContext) {
        handleSubmit(label);
        return;
      }
      // The starter came from a page context the user had dismissed. Attach
      // it again, and pass it to this send directly: `contexts.merged` in
      // handleSubmit is still the render before the restore.
      contexts.restoreDerived(pageContext);
      handleSubmit(label, undefined, [pageContext]);
    },
    [handleSubmit, contexts.restoreDerived]
  );

  const handleImprove = useCallback(async () => {
    const { agentId } = selection;
    const draft = pane.composerText;
    if (!agentId || draft.trim().length === 0 || isImproving) return;

    logger.debug('moo.improve.start', { agentId });
    analytics.track(MooAnalyticsEvents.IMPROVE_PROMPT_CLICKED, { agentId });
    setIsImproving(true);
    try {
      const improvedPrompt = await transport.improvePrompt({
        prompt: draft,
        agentId,
        contexts: contexts.merged,
      });
      // Guard against an empty / whitespace-only response — overwriting the
      // composer with nothing would silently destroy the user's draft.
      // Route through the same failure surface as a thrown error so the
      // toast + analytics ok:false fire consistently.
      if (!improvedPrompt || improvedPrompt.trim().length === 0) {
        throw new Error('improvePrompt returned empty response');
      }
      // The composer is a plain textarea: markdown would show as raw ** and
      // dashes, so the improved prompt lands as clean plain text.
      pane.setComposerText(markdownToPlainText(improvedPrompt));
      analytics.track(MooAnalyticsEvents.IMPROVE_PROMPT_COMPLETED, {
        agentId,
        ok: true,
      });
    } catch (err) {
      logger.error('moo.improve.error', { error: String(err) });
      toast?.error("Couldn't improve prompt");
      analytics.track(MooAnalyticsEvents.IMPROVE_PROMPT_COMPLETED, {
        agentId,
        ok: false,
      });
    } finally {
      setIsImproving(false);
    }
  }, [
    selection.agentId,
    pane.composerText,
    pane.setComposerText,
    contexts.merged,
    transport,
    logger,
    analytics,
    toast,
    isImproving,
  ]);

  return (
    <ChatSendProvider send={handleSubmit}>
      <AppendAssistantTurnProvider append={appendAssistantTurn}>
        <Box style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
          {hasMessages ? (
            <MessageList
              messages={chat.messages as any}
              isStreaming={isStreaming}
            />
          ) : (
            <EmptyState
              agent={selectedAgent}
              onStarterClick={handleStarterClick}
            />
          )}
        </Box>
        {errorState.error && (
          <Box p="sm" style={{ flexShrink: 0 }}>
            <Alert
              color="red"
              variant="light"
              title="Stream error"
              role="alert"
              withCloseButton
              onClose={clearError}
            >
              {errorState.error.message}
            </Alert>
          </Box>
        )}
        <Box style={{ flexShrink: 0 }}>
          <Composer
            value={pane.composerText}
            onChange={handleComposerChange}
            suggestion={nextPromptText}
            onDismissSuggestion={dismissNextPrompt}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            onImprove={handleImprove}
            disabled={!selection.agentId}
            agentName={selectedAgent?.name}
            isStreaming={isStreaming}
            isImproving={isImproving}
            chipsSlot={
              <>
                <UploadChipStrip
                  uploads={uploads.uploads}
                  onRemove={uploads.removeUpload}
                />
                <ContextChipList />
              </>
            }
            actionsSlot={
              <ConnectedAppsChipStrip
                webSearch={{
                  enabled: pane.webSearchEnabled,
                  onChange: handleWebSearchChange,
                }}
                toneSlot={
                  tones &&
                  tones.length > 0 && (
                    <ToneSelect
                      tones={tones}
                      tone={pane.toneId}
                      onChange={pane.setToneId}
                      disabled={
                        !selection.agentId || isStreaming || isImproving
                      }
                    />
                  )
                }
              />
            }
            onAttachFiles={uploads.addFiles}
            onVoiceInput={voiceInput?.onToggle}
            isVoiceListening={voiceInput?.isListening}
            isVoiceConnecting={voiceInput?.isConnecting}
            liveTranscript={voiceInput?.liveTranscript}
            consumeTranscript={voiceInput?.consumeTranscript}
            slashCommands={slashCommands}
          />
        </Box>
      </AppendAssistantTurnProvider>
    </ChatSendProvider>
  );
}

export function ChatPanel({
  paneId = 'main',
  uploads: uploadsProp,
}: ChatPanelProps) {
  const selection = useShellSelection();
  // Rules of hooks require this call every render even when `uploadsProp`
  // is supplied — its result is simply unused in that case. Standalone
  // callers (tests) that don't hoist their own `useUploads` still work via
  // this fallback, just without surviving this component's own remount.
  const internalUploads = useUploads(paneId);
  const uploads = uploadsProp ?? internalUploads;
  const { isDragActive, dropHandlers } = useFileDrop(uploads.addFiles);

  // Hydrate from server when a conversation is open. The parent (MooChat)
  // keys this component by conversationId so a switch remounts the panel
  // and starts a fresh history fetch.
  const history = useLoadConversation(selection.conversationId);

  const hasConversation = selection.conversationId !== null;
  const isHydrating = hasConversation && history.status === 'loading';
  const hasHydrationError = hasConversation && history.status === 'error';
  const isReady = !hasConversation || history.status === 'success';

  return (
    <Stack
      h="100%"
      gap={0}
      aria-label="Moo chat panel"
      role="region"
      className={surface.surface}
      style={{ overflow: 'hidden', position: 'relative' }}
      {...dropHandlers}
    >
      {isHydrating && (
        <Box style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <Center h="100%">
            <Loader size="sm" />
          </Center>
        </Box>
      )}
      {hasHydrationError && (
        <Box style={{ flex: 1, minHeight: 0, overflow: 'hidden' }} p="sm">
          <Alert
            color="red"
            variant="light"
            title="Couldn't load conversation"
            role="alert"
          >
            <Stack gap="xs">
              <Box>The conversation history failed to load.</Box>
              <Button
                size="xs"
                variant="light"
                onClick={() => selection.newConversation()}
              >
                Start a new chat
              </Button>
            </Stack>
          </Alert>
        </Box>
      )}
      {isReady && (
        <ChatStream
          paneId={paneId}
          initialMessages={history.data?.messages ?? []}
          uploads={uploads}
        />
      )}
      <ShellDrawer />
      {isDragActive && (
        <Box className={classes.dropOverlay} aria-hidden>
          Drop files to attach
        </Box>
      )}
    </Stack>
  );
}
